import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import QrCodeIcon from '@mui/icons-material/QrCode';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { LotType } from '../../common/model/type/lot.type';
import { Resource } from '../../common/model/type/resource.enum';
import { LOT_LIST_FILTERS } from '../../common/model/ui/lot-list-filters';
import { ListSortOption } from '../../common/model/ui/list-sort-option';
import { OrderBy } from '../../common/types';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { getListRange } from '../../common/utils/services/order-list-utils';
import { LotFields } from '../../firebase/document-field.enums';
import useAuthorized from '../../hooks/use-authorized';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import LotsService from '../../services/lots.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvLotKindSelectorModal from '../UI/molecules/OvLotKindSelectorModal';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvLotList from '../UI/organisms/OvLotList';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

const DEFAULT_LOT_LIST_ORDER_BY: OrderBy = {
  order_by: LotFields.created_at,
  order_dir: 'desc',
};

const getLotSortOptions = (
  translate: (key: string) => string
): ListSortOption[] => [
  {
    order_by: LotFields.created_at,
    order_dir: 'desc',
    label: translate('lots.sortOptions.createdNewest'),
  },
  {
    order_by: LotFields.created_at,
    order_dir: 'asc',
    label: translate('lots.sortOptions.createdOldest'),
  },
  {
    order_by: LotFields.name,
    order_dir: 'asc',
    label: translate('lots.sortOptions.nameAscending'),
  },
  {
    order_by: LotFields.name,
    order_dir: 'desc',
    label: translate('lots.sortOptions.nameDescending'),
  },
];

const AdminLotList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const applicationOperations = useAuthorized(Resource.Application);
  const lotOperations = useAuthorized(Resource.Lot);
  const lotCreateAllowed =
    lotOperations.create || applicationOperations.supervise;

  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters,
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  } = useListQueryParams(LOT_LIST_FILTERS, DEFAULT_LOT_LIST_ORDER_BY);
  const [isKindSelectorOpen, setIsKindSelectorOpen] = useState(false);

  const query = queryString.stringify({
    offset,
    limit: LotsService.LOT_LIST_LIMIT,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
    ...filterQueryParams,
  });
  const {
    data: paginatedLots,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-lots', query],
    queryFn: () => LotsService.getLots(query),
    placeholderData: keepPreviousData,
  });
  const lots = paginatedLots?.docs ?? [];
  const total = paginatedLots?.total ?? 0;

  const createLot = useMutation({
    mutationFn: (type: LotType) => LotsService.createLot(type),
    onSuccess: (lot) => navigate(`/lots/${lot.id}`),
  });

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('lots.resultRange', {
      ...getListRange(offset, LotsService.LOT_LIST_LIMIT, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('lots.states.errorTitle')}
          description={t('lots.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (lots.length > 0) {
      return (
        <OvLotList
          lots={lots}
          orderBy={orderBy?.order_by ?? ''}
          orderDir={orderBy?.order_dir ?? ''}
          onSort={changeOrderBy}
        />
      );
    }

    if (hasAppliedFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('lots.states.noResultsTitle')}
          description={t('lots.states.noResultsDescription')}
        >
          <OvCompactButton onClick={clearFilters}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<QrCodeIcon />}
        title={t('lots.states.emptyTitle')}
        description={
          lotCreateAllowed
            ? t('lots.states.emptyDescription')
            : t('lots.states.emptyReadOnlyDescription')
        }
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('lots.title')}
      icon={<QrCodeIcon />}
      headerAction={
        lotCreateAllowed && (
          <OvCompactButton
            icon={<AddCircleIcon />}
            onClick={() => setIsKindSelectorOpen(true)}
          >
            {t('lots.createNew')}
          </OvCompactButton>
        )
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        >
          <OvListSortSelect
            options={getLotSortOptions(t)}
            orderBy={orderBy?.order_by ?? ''}
            orderDir={orderBy?.order_dir ?? ''}
            onChange={changeOrderBy}
          />
        </OvListFilterBar>
      }
      context={
        createLot.error && (
          <OvErrorMessage message={getAxiosErrorMessage(createLot.error)} />
        )
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={LotsService.LOT_LIST_LIMIT}
      offset={offset}
      onPageChange={(_, page) =>
        setOffset((page - 1) * LotsService.LOT_LIST_LIMIT)
      }
      isBusy={isFetching || createLot.isPending}
    >
      {renderContent()}

      <OvLotKindSelectorModal
        isOpen={isKindSelectorOpen}
        saveSelection={(type: LotType) => createLot.mutate(type)}
        cancelSelection={() => setIsKindSelectorOpen(false)}
      />
    </OvRecordListPage>
  );
};

export default AdminLotList;
